/**
 * Utilitário para mascarar dados pessoais identificáveis (PII) em logs e analytics
 * Conforme LGPD e boas práticas de privacidade
 */

// Padrões regex para identificar diferentes tipos de PII
const PII_PATTERNS = {
  // Emails - formato: usuario@dominio.com
  email: /\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Z|a-z]{2,}\b/g,
  
  // CPFs - formatos: 123.456.789-00, 12345678900
  cpf: /\b\d{3}\.?\d{3}\.?\d{3}-?\d{2}\b/g,
  
  // CNPJs - formatos: 12.345.678/0001-90, 12345678000190
  cnpj: /\b\d{2}\.?\d{3}\.?\d{3}\/?\d{4}-?\d{2}\b/g,
  
  // Telefones - formatos: (11) 99999-9999, 11999999999, +55 11 99999-9999
  phone: /\b(?:\+?55\s?)?\(?(\d{2})\)?\s?(\d{4,5})-?(\d{4})\b/g,
  
  // CEPs - formatos: 12345-678, 12345678
  cep: /\b\d{5}-?\d{3}\b/g,
  
  // RG - formatos: 12.345.678-9, 123456789
  rg: /\b\d{1,2}\.?\d{3}\.?\d{3}-?\d{1,2}\b/g,
  
  // Cartões de crédito - formatos: 1234 5678 9012 3456, 1234567890123456
  creditCard: /\b\d{4}\s?\d{4}\s?\d{4}\s?\d{4}\b/g,
  
  // Tokens JWT - formato: eyJ...
  jwt: /\beyJ[A-Za-z0-9_-]*\.[A-Za-z0-9_-]*\.[A-Za-z0-9_-]*\b/g,
  
  // Senhas - qualquer string que contenha "password", "senha", "token", etc.
  password: /(?:password|senha|token|secret|key)\s*[:=]\s*["']?[^"'\s,}]+["']?/gi,
};

// Campos conhecidos que contêm PII
const PII_FIELD_NAMES = [
  'email', 'cpf', 'cnpj', 'phone', 'telefone', 'celular', 'mobile',
  'password', 'senha', 'token', 'access_token', 'refresh_token',
  'username', 'nome', 'name', 'full_name', 'first_name', 'last_name',
  'address', 'endereco', 'endereço', 'address_number', 'numero',
  'postcode', 'cep', 'zipcode', 'birth_date', 'data_nascimento',
  'date_of_birth', 'birthday', 'rg', 'document', 'documento',
  'credit_card', 'cartao', 'cartão', 'card_number', 'numero_cartao',
  'social_security', 'cpf_number', 'cnpj_number'
];

/**
 * Mascara uma string substituindo PII por placeholders
 */
export const maskPII = (text: string): string => {
  if (!text || typeof text !== 'string') return text;
  
  let masked = text;
  
  // Aplicar padrões regex
  masked = masked.replace(PII_PATTERNS.email, '[EMAIL_MASKED]');
  masked = masked.replace(PII_PATTERNS.cpf, '[CPF_MASKED]');
  masked = masked.replace(PII_PATTERNS.cnpj, '[CNPJ_MASKED]');
  masked = masked.replace(PII_PATTERNS.phone, '[PHONE_MASKED]');
  masked = masked.replace(PII_PATTERNS.cep, '[CEP_MASKED]');
  masked = masked.replace(PII_PATTERNS.rg, '[RG_MASKED]');
  masked = masked.replace(PII_PATTERNS.creditCard, '[CARD_MASKED]');
  masked = masked.replace(PII_PATTERNS.jwt, '[JWT_MASKED]');
  masked = masked.replace(PII_PATTERNS.password, '[PASSWORD_MASKED]');
  
  return masked;
};

/**
 * Mascara um objeto removendo ou mascarando campos que contêm PII
 */
export const maskObjectPII = (obj: any, deep: boolean = true): any => {
  if (obj === null || obj === undefined) return obj;
  
  // Se for uma string, aplicar mascaramento
  if (typeof obj === 'string') {
    return maskPII(obj);
  }
  
  // Se for array, processar cada item
  if (Array.isArray(obj)) {
    return obj.map(item => maskObjectPII(item, deep));
  }
  
  // Se for objeto, processar cada propriedade
  if (typeof obj === 'object') {
    const masked: any = {};
    
    for (const [key, value] of Object.entries(obj)) {
      const lowerKey = key.toLowerCase();
      
      // Verificar se o nome do campo contém PII
      const isPIIField = PII_FIELD_NAMES.some(piiField => 
        lowerKey.includes(piiField) || piiField.includes(lowerKey)
      );
      
      if (isPIIField) {
        // Mascarar completamente campos PII
        masked[key] = '[PII_MASKED]';
      } else if (deep && (typeof value === 'object' || Array.isArray(value))) {
        // Recursivamente mascarar objetos aninhados
        masked[key] = maskObjectPII(value, deep);
      } else if (typeof value === 'string') {
        // Mascarar strings que podem conter PII
        masked[key] = maskPII(value);
      } else {
        // Manter outros tipos como estão
        masked[key] = value;
      }
    }
    
    return masked;
  }
  
  // Para outros tipos primitivos, retornar como estão
  return obj;
};

/**
 * Cria um objeto de log seguro removendo PII
 */
export const createSafeLogData = (data: any): any => {
  if (!data) return data;
  
  // Se for um objeto com propriedades conhecidas, mascarar seletivamente
  if (typeof data === 'object' && !Array.isArray(data)) {
    return maskObjectPII(data, true);
  }
  
  // Para outros tipos, aplicar mascaramento básico
  return maskObjectPII(data, false);
};

/**
 * Sanitiza uma mensagem de log removendo PII
 */
export const sanitizeLogMessage = (message: string): string => {
  if (!message || typeof message !== 'string') return message;
  
  return maskPII(message);
};

/**
 * Verifica se um objeto contém PII
 */
export const containsPII = (obj: any): boolean => {
  if (!obj) return false;
  
  const stringified = JSON.stringify(obj);
  
  // Verificar se contém algum padrão PII
  return Object.values(PII_PATTERNS).some(pattern => 
    pattern.test(stringified)
  );
};

/**
 * Cria um resumo seguro de dados do usuário para logs
 */
export const createUserSummary = (user: any): any => {
  if (!user) return null;
  
  return {
    id: user.id || '[USER_ID]',
    type: user.type || user.userType || '[USER_TYPE]',
    hasEmail: !!(user.email),
    hasPhone: !!(user.phone || user.mobile || user.telefone),
    createdAt: user.createdAt || user.created_at,
    lastLogin: user.lastLogin || user.last_login,
    // Não incluir dados pessoais reais
  };
};

/**
 * Utilitário para logs seguros de autenticação
 */
export const createAuthLogData = (data: any): any => {
  return {
    method: data.method || '[AUTH_METHOD]',
    success: data.success || false,
    timestamp: new Date().toISOString(),
    userAgent: typeof window !== 'undefined' ? window.navigator.userAgent : '[USER_AGENT]',
    ipAddress: '[IP_MASKED]', // IP sempre mascarado
    sessionId: data.sessionId ? '[SESSION_MASKED]' : undefined,
    // Não incluir credenciais ou tokens
  };
};

/**
 * Utilitário para logs seguros de API
 */
export const createApiLogData = (data: any): any => {
  return {
    endpoint: data.endpoint || '[ENDPOINT]',
    method: data.method || '[METHOD]',
    status: data.status || '[STATUS]',
    duration: data.duration || '[DURATION]',
    timestamp: new Date().toISOString(),
    // Não incluir dados de request/response que podem conter PII
  };
};

export default {
  maskPII,
  maskObjectPII,
  createSafeLogData,
  sanitizeLogMessage,
  containsPII,
  createUserSummary,
  createAuthLogData,
  createApiLogData,
};
